import { Component, NgZone, OnInit, ViewChild, TemplateRef, ChangeDetectorRef } from '@angular/core';
import { NgbModal, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { ApiService } from '../../../../shared/services/api.service';
import { ColDef } from 'ag-grid-community';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  standalone: false,
  selector: 'app-country-dynamicfields',
  templateUrl: './dynamicfields.component.html',
  styleUrl: './dynamicfields.component.scss'
})
export class CountryDynamicFieldsComponent implements OnInit {
  @ViewChild('dynamicFieldOffcanvas') dynamicFieldOffcanvas: TemplateRef<any> | undefined;
  
  countryId: string = '';
  dynamicFields: any[] = [];
  originalFields: any[] = []; // To track original state for unsaved changes detection
  gridApi: any;
  count = 0;
  apiUrl: any;
  loading: boolean = false;
  error: string = '';
  isEditing: boolean = false;
  isAdding: boolean = false;
  newField: any = {
    priority: 0,
    label: '',
    datatype: 'text',
    validationPattern: '',
    validationMessage: '',
    isRequired: false,
    isActive: true,
    options: [],
    isMasked: false,
    matchWith: ''
  };

  dataTypes = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'select', label: 'Select' },
    { value: 'date', label: 'Date' },
  ];

  gridOptions: any = {
    paginationPageSize: 15,
    cacheBlockSize: 15,
    pagination: true,
    rowModelType: 'infinite',
    paginationPageSizeSelector: [15, 20, 50, 100],
    rowSelection: 'multiple',
    context: {
      componentParent: this,
    },
  };

   columnDefs: ColDef[] = [
    {
      headerName: 'Action',
      field: 'action',
      width: 150,
      cellRenderer: (params: any) => {
        const container = document.createElement('div');
        container.classList.add('d-flex', 'gap-1');
        
        const editBtn = document.createElement('button');
        editBtn.classList.add('btn', 'btn-sm', 'btn-primary');
        editBtn.innerHTML = '<i class="bi bi-pencil-fill"></i>';
        editBtn.addEventListener('click', () => this.startEditing(params.data));
        
        const deleteBtn = document.createElement('button');
        deleteBtn.classList.add('btn', 'btn-sm', 'btn-danger');
        deleteBtn.innerHTML = '<i class="bi bi-trash-fill"></i>';
        deleteBtn.addEventListener('click', () => this.onDelete(params));
        
        container.appendChild(editBtn);
        container.appendChild(deleteBtn);
        
        return container;
      },
    },
        {
      headerName: 'Status',
      field: 'isActive',
      editable: false,
      filter: true,
      width: 100,
      valueFormatter: (params: any) => {
        return params.value ? 'Active' : 'Inactive';
      },
      cellClass: (params: any) => {
        return params.value ? 'text-success' : 'text-danger';
      }
    },
    
    {
      headerName: 'Priority',
      field: 'priority',
      editable: false,
      filter: true,
      width: 100,
    },
    {
      headerName: 'Label',
      field: 'label',
      editable: false,
      filter: true,
    },
    {
      headerName: 'Data Type',
      field: 'datatype',
      editable: false,
      filter: true,
      width: 120,
    },
    {
      headerName: 'Masked',
      field: 'isMasked',
      editable: false,
      filter: true,
      width: 100,
      valueFormatter: (params: any) => {
        return params.value ? 'Yes' : 'No';
      },
      cellClass: (params: any) => {
        return params.value ? 'text-secondary' : 'text-muted';
      }
    },
    {
      headerName: 'Match With',
      field: 'matchWith',
      editable: false,
      filter: true,
      width: 150,
    },
    {
      headerName: 'Validation Pattern',
      field: 'validationPattern',
      editable: false,
      filter: true,
    },
    {
      headerName: 'Validation Message',
      field: 'validationMessage',
      editable: false,
      filter: true,
    },
    {
      headerName: 'Required',
      field: 'isRequired',
      editable: false,
      filter: true,
      width: 100,
      valueFormatter: (params: any) => {
        return params.value ? 'Yes' : 'No';
      },
      cellClass: (params: any) => {
        return params.value ? 'text-primary' : 'text-muted';
      }
    },

  ];

  constructor(
    private modalService: NgbModal,
    private offcanvasService: NgbOffcanvas,
    private _api: ApiService,
    private zone: NgZone,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  countryDetails: any = {};

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.countryId = params['id'];
      this.getCountryDetails();
      this.getDynamicFields();
    });
  }

  getCountryDetails() {
    const vm = this;
    vm.loading = true;
    const apiUrl = `/admin/get-data?type=country&_id=${this.countryId}`;
    vm._api.getData(apiUrl).subscribe((res: any) => {
      if (res && !res.error && res.data.length > 0) {
        vm.countryDetails = res.data[0];
      }
      vm.loading = false;
    }, (error: any) => {
      vm.loading = false;
      console.error('Error fetching country details:', error);
    });
  }

  getDynamicFields() {
    const vm = this;
    vm.loading = true;
    const apiUrl = `/admin/countries/${this.countryId}/dynamic`;
    vm._api.getData(apiUrl).subscribe((res: any) => {
      vm.loading = false;
      if (res && !res.error && res.data) {
        const rawSettings = res.data.settings || [];
        vm.dynamicFields = rawSettings;
        vm.originalFields = JSON.parse(JSON.stringify(vm.dynamicFields)); // Save original state
        // Refresh the grid
        if (vm.gridApi) {
          setTimeout(() => {
            vm.gridApi.refreshInfiniteCache();
          }, 100);
        }
      }
    }, (error: any) => {
      vm.loading = false;
      console.error('Error fetching dynamic fields:', error);
    });
  }

  onGridReady(params: any) {
    const vm = this;
    vm.gridApi = params.api;
    vm.gridApi.sizeColumnsToFit();
  }

  createDataSource() {
    const vm = this;
    const datasource = {
      getRows: (params: any) => {
        const startRow = params.startRow;
        const endRow = params.endRow;

        // Since we're fetching all dynamic fields at once, we can slice the data
        const rowsThisPage = vm.dynamicFields.slice(startRow, endRow);
        const lastRow = vm.dynamicFields.length;

        params.successCallback(rowsThisPage, lastRow);
      },
    };
    return datasource;
  }

  // In-place editing methods
  startAdding() {
    this.isAdding = true;
    this.newField = {
      _id: this.generateTemporaryId(), // Generate temporary ID for new fields
      priority: this.dynamicFields.length + 1,
      label: '',
      datatype: 'text',
      validationPattern: '',
      validationMessage: '',
      isRequired: false,
      isActive: true,
      options: [],
      isMasked: false,
      matchWith: ''
    };
    this.cdr.detectChanges();
  }

  cancelAdd() {
    this.isAdding = false;
    this.newField = {
      _id: '',
      priority: 0,
      label: '',
      datatype: 'text',
      validationPattern: '',
      validationMessage: '',
      isRequired: false,
      isActive: true,
      options: [],
      isMasked: false,
      matchWith: ''
    };
    this.cdr.detectChanges();
  }

  startEditing(field: any) {
    field.isEditing = true;
    // Create a copy of the field for editing to allow canceling without affecting the original
    field.originalData = { ...field };
  }

  cancelEditing(field: any) {
    // Restore original data
    Object.assign(field, field.originalData);
    field.isEditing = false;
    delete field.originalData;
  }

  saveEdit(field: any) {
    // Validate field
    if (!field.label.trim()) {
      this.error = 'Label is required';
      return;
    }

    if (field.datatype === 'select' && (!field.options || field.options.length === 0)) {
      this.error = 'Select field must have at least one option';
      return;
    }

    if (field.datatype === 'select' && field.options.some((opt: any) => !opt.label.trim() || !opt.value.trim())) {
      this.error = 'Select options must have both label and value';
      return;
    }

    // Validate regex pattern if provided and not select/date type
    if (field.datatype !== 'select' && field.datatype !== 'date' && field.validationPattern) {
      try {
        new RegExp(field.validationPattern);
      } catch (e) {
        this.error = 'Invalid regex pattern';
        return;
      }
    }

    // Sort select options alphabetically by label
    if (field.datatype === 'select' && field.options && field.options.length > 0) {
      field.options = field.options.sort((a, b) => a.label.localeCompare(b.label));
    }

    field.isEditing = false;
    delete field.originalData;
  }

  saveDynamicField() {
    this.error = '';

    // Validate field
    if (!this.newField.label.trim()) {
      this.error = 'Label is required';
      return;
    }

    if (this.newField.datatype === 'select' && this.newField.options.length === 0) {
      this.error = 'Select field must have at least one option';
      return;
    }

    if (this.newField.datatype === 'select' && this.newField.options.some((opt: any) => !opt.label.trim() || !opt.value.trim())) {
      this.error = 'Select options must have both label and value';
      return;
    }

    // Validate regex pattern if provided and not select/date type
    if (this.newField.datatype !== 'select' && this.newField.datatype !== 'date' && this.newField.validationPattern) {
      try {
        new RegExp(this.newField.validationPattern);
      } catch (e) {
        this.error = 'Invalid regex pattern';
        return;
      }
    }

    // Sort select options alphabetically by label
    const fieldToAdd = {
      ...this.newField
    };
    if (fieldToAdd.datatype === 'select' && fieldToAdd.options && fieldToAdd.options.length > 0) {
      fieldToAdd.options = fieldToAdd.options.sort((a, b) => a.label.localeCompare(b.label));
    }

    // Add new field locally
    this.dynamicFields.push(fieldToAdd);
    this.updatePriorities(); // Update priorities for all fields
    this.isAdding = false;
    this.newField = {
      _id: '',
      priority: 0,
      label: '',
      datatype: 'text',
      validationPattern: '',
      validationMessage: '',
      isRequired: false,
      isActive: true,
      options: [],
      isMasked: false,
      matchWith: ''
    };
    this.cdr.detectChanges();
  }

  generateTemporaryId(): string {
    return 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Add new select option
  addSelectOption(field: any) {
    if (!field.options) {
      field.options = [];
    }
    field.options.push({ label: '', value: '', imageUrl: '' });
  }

  // Remove select option
  removeSelectOption(field: any, index: number) {
    if (field.options && field.options.length > 1) {
      field.options.splice(index, 1);
    }
  }

  // Get select options labels as comma-separated string
  getSelectOptionsLabels(options: any[]): string {
    return options.map(opt => opt.label).join(', ');
  }

  // Get non-current fields for matchWith dropdown
  getNonCurrentFields(currentField: any): any[] {
    return this.dynamicFields.filter(f => f._id !== currentField._id && f.label);
  }

  // Validate date in dd/mm/yyyy format
  validateDate(dateStr: string): boolean {
    const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
    if (!regex.test(dateStr)) {
      return false;
    }

    const parts = dateStr.split('-');
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  }

  // Handle data type change
  onDataTypeChange(field: any, newType: string) {
    // Clear validation fields when changing to select or date type
    if (newType === 'select' || newType === 'date') {
      field.validationPattern = '';
      field.validationMessage = '';
    }

    // Initialize options array when changing to select type
    if (newType === 'select' && !field.options) {
      field.options = [];
      field.options.push({ label: '', value: '', imageUrl: '' });
    }
  }

  onDelete(params: any) {
    if (confirm('Are you sure you want to delete this dynamic field?')) {
      // Delete field locally
      const index = this.dynamicFields.findIndex(field => field._id === params.data._id);
      if (index !== -1) {
        this.dynamicFields.splice(index, 1);
        this.updatePriorities(); // Update priorities for all fields
      }
    }
  }

  onDragDrop(event: any) {
    moveItemInArray(this.dynamicFields, event.previousIndex, event.currentIndex);
    this.updatePriorities(); // Update priorities after reordering
  }

  updatePriorities() {
    this.dynamicFields.forEach((field, index) => {
      field.priority = index + 1;
    });
  }

  // Check if there are unsaved changes
  hasUnsavedChanges(): boolean {
    return JSON.stringify(this.originalFields) !== JSON.stringify(this.dynamicFields);
  }

  // Save all changes to API
  saveAllChanges() {
    this.loading = true;
    this.error = '';

    // Sort select options alphabetically by label
    const fieldsToSave = this.dynamicFields.map(field => {
      if (field.datatype === 'select' && field.options && field.options.length > 0) {
        return {
          ...field,
          options: [...field.options].sort((a, b) => a.label.localeCompare(b.label))
        };
      }
      return field;
    });

    const apiUrl = `/admin/countries/${this.countryId}/dynamic`;
    this._api.putData(apiUrl, {
      settings: fieldsToSave
    }).subscribe((saveRes: any) => {
      this.loading = false;
      if (saveRes && !saveRes.error) {
        // Update the dynamicFields with sorted options
        this.dynamicFields = fieldsToSave;
        this.originalFields = JSON.parse(JSON.stringify(this.dynamicFields)); // Update original state
      } else {
        this.error = saveRes?.message || 'Failed to save changes';
      }
    }, (error: any) => {
      this.loading = false;
      this.error = 'Failed to save changes';
    });
  }

  // Revert to original state
  revertChanges() {
    if (confirm('Are you sure you want to revert all unsaved changes?')) {
      this.dynamicFields = JSON.parse(JSON.stringify(this.originalFields));
    }
  }

  goBack() {
    if (this.hasUnsavedChanges()) {
      if (confirm('You have unsaved changes. Are you sure you want to leave?')) {
        this.router.navigate(['/app/country']);
      }
    } else {
      this.router.navigate(['/app/country']);
    }
  }
}
